class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
  this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

  this.input.addEventListener('focus', this.onFocus.bind(this));
  this.addEventListener('focusout', this.onFocusOut.bind(this));
  this.addEventListener('keyup', this.onKeyup.bind(this));
  this.addEventListener('keydown', this.onKeydown.bind(this));

  /* NOBL — strict predictive suggestion handling */
  this.addEventListener('click', this.onPredictiveResultClick.bind(this));
}

  getQuery() {
    return this.input.value.trim();
  }

  buildNoblSearchQuery(value) {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => {
      const cleanTerm = term
        .replace(/^title:/i, '')
        .replace(/\*+$/, '');

      const safeTerm =
        cleanTerm.replace(/([\\:()])/g, '\\$1');

      return `title:${safeTerm}*`;
    })
    .join(' ');
}

navigateToNoblSearch(value) {
  const query = value.trim();

  if (!query.length) return;

  const scopedQuery =
    this.buildNoblSearchQuery(query);

  const destination =
    new URL(routes.search_url, window.location.origin);

  destination.searchParams.set('q', scopedQuery);
  destination.searchParams.set('type', 'product');
  destination.searchParams.set('sort_by', 'relevance');

  window.location.assign(
    destination.pathname +
    destination.search
  );
}

onPredictiveResultClick(event) {
  /*
   * Product links remain untouched.
   *
   * Only intercept the textual search suggestions
   * in the left Suggestions column.
   */
  const suggestionLink =
    event.target.closest(
      '#predictive-search-results-queries-list a'
    );

  if (!suggestionLink) return;

  const suggestionText =
    suggestionLink
      .querySelector('[aria-label]')
      ?.getAttribute('aria-label')
      ?.trim();

  if (!suggestionText) return;

  event.preventDefault();

  this.navigateToNoblSearch(suggestionText);
}

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
  const query = this.getQuery();

  if (!query.length) {
    event.preventDefault();
    return;
  }

  /*
   * If a PRODUCT link is selected, allow the product
   * navigation logic to handle it normally.
   */
  if (
    this.querySelector(
      '[aria-selected="true"] a:not(#predictive-search-results-queries-list a)'
    )
  ) {
    event.preventDefault();
    return;
  }

  /*
   * The dedicated NOBL main search page already has its
   * own strict submit handler.
   */
  if (this.closest('.nobl-main-search')) {
    return;
  }

  /*
   * Header / global predictive searches:
   * send the customer into the same strict
   * title-only NOBL search system.
   */
  event.preventDefault();

  this.navigateToNoblSearch(query);
}

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  filterNoblPredictiveProducts(resultsMarkup, searchTerm) {
  const container = document.createElement('div');
  container.innerHTML = resultsMarkup;

  const searchTerms = searchTerm
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) =>
      term
        .replace(/^title:/i, '')
        .replace(/\*+$/, '')
    );

  const productItems = Array.from(
    container.querySelectorAll(
      '#predictive-search-results-products-list .predictive-search__list-item'
    )
  );

  let matchingProductCount = 0;

  productItems.forEach((item) => {
    const titleElement =
      item.querySelector('.predictive-search__item-heading');

    const productTitle =
      titleElement?.textContent
        ?.trim()
        ?.toLowerCase() || '';

    const titleWords = productTitle
      .split(/[\s\-–—_/®™©()]+/)
      .filter(Boolean);

    const matchesAllTerms =
      searchTerms.every((term) =>
        titleWords.some((word) =>
          word.startsWith(term)
        )
      );

    if (!matchesAllTerms) {
      item.remove();
    } else {
      matchingProductCount++;
    }
  });

  const productsList =
    container.querySelector(
      '#predictive-search-results-products-list'
    );

  /*
   * If Shopify returned predictive products but none
   * actually match the NOBL title rule, remove the
   * empty Products section.
   */
  if (
    productsList &&
    matchingProductCount === 0
  ) {
    const productsSection =
      productsList.parentElement;

    productsSection?.remove();
  }

  /*
   * Correct the result count after removing
   * Shopify's broader predictive matches.
   */
  const suggestionCount =
    container.querySelectorAll(
      '#predictive-search-results-queries-list .predictive-search__list-item'
    ).length;

  const totalResultsElement =
    container.querySelector(
      '[data-total-results]'
    );

  if (totalResultsElement) {
    totalResultsElement.dataset.totalResults =
      String(
        suggestionCount +
        matchingProductCount
      );
  }

  return container.innerHTML;
}

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const predictiveSearchUrl =
  `${routes.predictive_search_url}` +
  `?q=${encodeURIComponent(searchTerm)}` +
  `&resources[type]=query,product` +
  `&resources[options][fields]=title` +
  `&resources[limit]=5` +
  `&resources[limit_scope]=each` +
  `&section_id=predictive-search`;

fetch(predictiveSearchUrl, {
  signal: this.abortController.signal,
})
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
  const resultsMarkup = new DOMParser()
    .parseFromString(text, 'text/html')
    .querySelector('#shopify-section-predictive-search').innerHTML;

  const filteredResultsMarkup =
    this.filterNoblPredictiveProducts(
      resultsMarkup,
      searchTerm
    );

  // Save the already-filtered NOBL results
  this.allPredictiveSearchInstances.forEach(
    (predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] =
        filteredResultsMarkup;
    }
  );

  this.renderSearchResults(
    filteredResultsMarkup
  );

  searchDeferred?.resolve({
    totalCount: this.getTotalResultCount()
  });
})
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
